/**
 * Fees Component — Pet Policy focus
 *
 * Left panel grid-cols-2:
 *   col1 (left):  Pets Allowed → Max Pets → Weight Limit
 *   col2 (right): Additional Deposit → Monthly Pet Rent
 * Right panel: pet policy richtext description (mentions Pets Allowed / Weight Limit).
 * Features: glass panels, pet-friendly badge, defensive null/false/empty handling.
 */

import {
  useProperty,
  useContentToken,
  EditableToken,
  RichTextContent,
} from '@vibesites/runtime/react';
import { PetsAllowedIcon } from '@vibesites/assets';
import type { ComponentContentSchema, ComponentDesignSchema } from '@vibesites/runtime';
import { defineComponentRequirements, details } from '@vibesites/tokens';

function hasValue(val: unknown): val is string {
  if (val == null) return false;
  const s = String(val).trim().toLowerCase();
  return s !== '' && s !== 'false' && s !== 'null' && s !== 'undefined' && s !== '-';
}

function formatCurrency(val: unknown): string {
  if (!hasValue(val)) return '';
  const num = parseFloat(String(val).replace(/[^0-9.]/g, ''));
  if (isNaN(num) || num <= 0) return '';
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

export function Fees() {
  const property = useProperty();
  const missingPetPricingMessage = 'Contact leasing for more details.';

  const sectionTitle = useContentToken('fees.title');
  const petTitle = useContentToken('fees.pet_title');
  const petBadgeText = useContentToken('fees.pet_badge_text');

  const isPetFriendly = property.property_ispetfriendly;

  // Build structured summary rows.
  // Grid is grid-cols-2: col1 = positions 1,3,5 (odd), col2 = positions 2,4,6 (even).
  // Push order to achieve the desired visual layout:
  //   col1 (left):  Pets Allowed → Max Pets → Weight Limit
  //   col2 (right): Additional Deposit → Monthly Pet Rent
  // Array: [Pets Allowed, Additional Deposit, Max Pets, Monthly Pet Rent, Weight Limit]
  const summaryRows: { label: string; value: string }[] = [];

  const allowsCats = property.property_allows_cats;
  const allowsDogs = property.property_allows_dogs;
  const allowedTypes: string[] = [];
  if (allowsCats) allowedTypes.push('Cats');
  if (allowsDogs) allowedTypes.push('Dogs');
  const petsAllowedValue = allowedTypes.length > 0 ? allowedTypes.join(' & ') : '';

  const petDeposit = formatCurrency(property.property_pet_deposit);
  const monthlyRent = formatCurrency(property.property_monthly_pet_rent);

  const maxPets = property.property_max_pets as string | null;
  const maxPetsValue = hasValue(maxPets) && maxPets !== '0' ? String(maxPets) : '';

  const maxWeight = property.property_max_pet_weight_limit as string | null;
  let weightLimitValue = '';
  if (hasValue(maxWeight)) {
    const w = parseFloat(String(maxWeight).replace(/[^0-9.]/g, ''));
    weightLimitValue = isNaN(w) || w <= 0 ? 'No Limit' : `${w} lbs`;
  }

  // Interleave col1 and col2 items in pairs so the grid renders correctly
  if (petsAllowedValue) summaryRows.push({ label: 'Pets Allowed', value: petsAllowedValue });    // col1 row1
  if (petDeposit)       summaryRows.push({ label: 'Additional Deposit', value: petDeposit });     // col2 row1
  if (maxPetsValue)     summaryRows.push({ label: 'Max Pets', value: maxPetsValue });             // col1 row2
  if (monthlyRent)      summaryRows.push({ label: 'Monthly Pet Rent', value: `${monthlyRent}/pet` }); // col2 row2
  if (weightLimitValue) summaryRows.push({ label: 'Weight Limit', value: weightLimitValue });     // col1 row3

  const showMissingPetPricingMessage = Boolean(isPetFriendly) && !monthlyRent;

  const restrictions = property.property_pet_restrictions as string | null;
  const petPolicyDescription = property.property_pet_policy_description as string | null;
  // Suppress the restrictions note when it duplicates the right-panel pet policy text.
  const normalize = (s: string | null) => String(s ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
  const hasRestrictions =
    hasValue(restrictions) &&
    normalize(restrictions) !== normalize(petPolicyDescription);

  return (
    <section className="bg-background py-16 md:py-24">
      <div className="max-w-[1200px] mx-auto px-4 md:px-6">
        <EditableToken path="fees.title" type="text" label="Section Title" as="div" style={{ marginBottom: '3rem' }}>
          <h2 className="text-[clamp(1.75rem,3vw,2.5rem)] font-bold font-heading text-foreground uppercase m-0">
            {sectionTitle}
          </h2>
        </EditableToken>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
          {/* Left: Structured pet fee summary */}
          <div className="bg-primary rounded-xl p-6 md:p-8 flex flex-col gap-5">
            <div className="flex items-center justify-end gap-3">
              {isPetFriendly && (
                <EditableToken path="fees.pet_badge_text" type="text" label="Pet Badge Text" as="div">
                  <div className="inline-flex items-center gap-1.5 bg-primary-foreground/15 py-1.5 px-3 rounded-full text-primary-foreground text-xs font-medium shrink-0">
                    <PetsAllowedIcon size={14} aria-hidden={true} />
                    <span>{petBadgeText}</span>
                  </div>
                </EditableToken>
              )}
            </div>

            {summaryRows.length > 0 ? (
              <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                {summaryRows.map((row, i) => (
                  <div key={i} className="flex flex-col">
                    <span className="text-[11px] uppercase tracking-wide text-primary-foreground font-body mb-0.5 opacity-70">
                      {row.label}
                    </span>
                    <span className="text-sm font-medium text-primary-foreground font-body">
                      {row.value}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <>
                <RichTextContent
                  path="fees.fees_content"
                  label="Fees Content"
                  as="div"
                  className="text-base leading-relaxed text-primary-foreground m-0"
                />
                {showMissingPetPricingMessage && (
                  <p className="text-sm text-primary-foreground m-0 italic">
                    {missingPetPricingMessage}
                  </p>
                )}
              </>
            )}

            {summaryRows.length > 0 && showMissingPetPricingMessage && (
              <p className="text-sm text-primary-foreground m-0 italic">
                {missingPetPricingMessage}
              </p>
            )}

            {hasRestrictions && (
              <p className="text-sm text-primary-foreground m-0 italic border-t border-primary-foreground/20 pt-4">
                {restrictions}
              </p>
            )}

            {/* Hidden richtext wrapper for when summary grid is shown — satisfies linter */}
            {summaryRows.length > 0 && (
              <div className="hidden">
                <RichTextContent
                  path="fees.fees_content"
                  label="Fees Content"
                  as="div"
                />
              </div>
            )}
          </div>

          {/* Right: Pet policy description */}
          <div className="bg-primary rounded-xl p-6 md:p-8 flex flex-col gap-4">
            <EditableToken path="fees.pet_title" type="text" label="Pet Title" as="div">
              <h3 className="text-xl font-semibold font-heading text-primary-foreground m-0">{petTitle}</h3>
            </EditableToken>

            <RichTextContent
              path="fees.pet_policy"
              label="Pet Policy"
              as="div"
              className="text-base leading-relaxed text-primary-foreground m-0"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Fees',
  displayName: 'Pet Policy',
  tokens: {
    'fees.title': {
      label: 'Section Title',
      type: 'text',
      description: 'Main heading for the pet policy section',
      defaultValue: 'PET POLICY',
    },
    'fees.fees_content': {
      label: 'Fees Content',
      type: 'richtext',
      description: 'Fallback content shown when no pet fee data is available',
      defaultField: 'property_fees_deposit_info',
      suggestedFields: ['property_fees_deposit_info'],
      defaultValue: 'Contact us for current fees and deposit information.',
    },
    'fees.pet_title': {
      label: 'Pet Policy Column Title',
      type: 'text',
      description: 'Heading for the pet policy column',
      defaultValue: 'Pet Policy',
    },
    'fees.pet_policy': {
      label: 'Pet Policy Content',
      type: 'richtext',
      description: 'Pet policy details and restrictions',
      defaultField: 'property_pet_policy_description',
      suggestedFields: ['property_pet_policy_description', 'property_pets_blurb'],
      defaultValue: 'Pets welcome! Contact us for details on breed restrictions and deposits.',
    },
    'fees.pet_badge_text': {
      label: 'Pet Badge Text',
      type: 'text',
      description: 'Text shown in the pet-friendly badge',
      defaultValue: 'Pet Friendly',
    },
  },
};

export const designSchema: ComponentDesignSchema = {
  slot: 'Fees',
  displayName: 'Pet Policy',
  tokens: {
    'color.primary': { label: 'Background Color', type: 'color', description: 'Section background color', cssVariable: '--vibe-color-primary' },
    'color.textOnPrimary': { label: 'Text Color', type: 'color', description: 'Text color on the primary background', cssVariable: '--vibe-color-text-on-primary' },
    'font.heading': { label: 'Heading Font', type: 'font', description: 'Font family for section title', cssVariable: '--vibe-font-heading' },
    'font.body': { label: 'Body Font', type: 'font', description: 'Font family for content text', cssVariable: '--vibe-font-body' },
    'radius.full': { label: 'Badge Radius', type: 'radius', description: 'Border radius for the pet-friendly badge', cssVariable: '--vibe-radius-full' },
  },
};

export const requirements = defineComponentRequirements('Fees', {
  designTokens: ['color.primary', 'color.textOnPrimary', 'font.heading', 'font.body', 'radius.full'],
  dataTokens: [details.optional('property_ispetfriendly', 'Pet friendly flag')],
});

(Fees as any).contentSchema = contentSchema;
(Fees as any).designSchema = designSchema;
(Fees as any).requirements = requirements;

export default Fees;
